import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import {
  QIcon_close_default,
  QIcon_filter,
  QIcon_person,
  QIcon_question_filled,
  QIcon_refresh,
  QIcon_search_default,
} from '@repo/icons/index'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { CurrentUser } from '../../../../../model/Common/common.interface'
import { ITutor, TutorKeyType } from '../../../../../model/tutor.interface'
import apis from '../../../../../modules/apis'
import { axiosPost } from '../../../../../modules/axiosHelper'
import { APP_SCREEN_NAME, STACK_ACTIVITY_NAME } from '../../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../../modules/hooks/common/useIsWebview'
import { useOnclickHeartTutor } from '../../../../../modules/hooks/common/useOnclickHeartTutor'
import { useTutorProfileSideModal } from '../../../../../modules/hooks/common/useTutorProfileSideModal'
import useReturnFullUrl from '../../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_book_lessons, n_tutor_lists } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import { COLORS, TUTOR_TYPE } from '../../../../../modules/vars'
import { useTutorStack } from '../../../../../pages/[lang]/webview/tutors'
import { useSidePopup } from '../../../../basic/popup/SidePopup'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import Pagination from '../../../../common/Pagination'
import TutorCard from '../../../../common/TutorCard'
import CompletedLesson from '../../../../new-portal/lessons/schedules/CompletedLesson'
import { FilterBottomSheet, FilterPopup } from '../FilterPopup'
import { TUTOR_PROFILE_TAB } from '../TutorBody'

interface ITutorListPropsType {
  currentUser: CurrentUser
  initialKeyword?: string
}

const TutorSearchListBody = ({ currentUser, initialKeyword }: ITutorListPropsType) => {
  const { t } = useTranslation()
  const isWebview = useIsWebview()
  const type = TUTOR_TYPE.ALL_TUTOR
  const [showFilter, setShowFilter] = useState(false)
  const isMobile = useIsMobile()
  const { useTutorFlow } = useTutorStack()
  const { push } = useTutorFlow()
  const router = useAppRouter({ stackPush: push })
  const [isFiltered, setIsFiltered] = useState(false)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState(initialKeyword || router?.query?.keyword)
  const getFullUrl = useReturnFullUrl()
  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setKeyword(e.target.value)
  }
  const handleClose = () => {
    setKeyword('')
  }
  const [selectedTutor, setSelectedTutor] = useState()
  const [result, setResult] = useState()
  const showTutorProfileSideModal = useTutorProfileSideModal({
    overlayClose: true,
    bgWhite: false,
  })
  const sidePopup = useSidePopup()
  const onCloseSidePopup = () => {
    sidePopup.close('tutor-popup')
  }
  useEffect(() => {
    if (result) {
      onCloseSidePopup()
    }
  }, [result])
  const handleSchedule = (e, tutor, tab) => {
    if (isWebview) {
      router.push(urls.student.newPortal.tutors.detail.as(tutor.id, tab), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.tutors.detail.as(tutor.id, tab)),
        },
      })
    } else {
      setSelectedTutor(tutor.id)
      showTutorProfileSideModal(
        e,
        tutor.id,
        tutor.is_featured_tutor,
        () => {
          setSelectedTutor(null)
        },
        tab,
        setResult
      )
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      router.push(urls.student.newPortal.tutors.search(keyword), {
        stackActivity: STACK_ACTIVITY_NAME.TUTOR_SEARCH_ACTIVITY,
        stackSendData: { keyword: keyword },
      })
    }
  }
  const focusRef = useRef(null)

  const getTutorType = (type: TutorKeyType) => {
    return 'all'
  }

  const [tutors, setTutors] = useState<ITutor[]>([])

  useEffect(() => {
    fetchTutorList(1)
  }, [router?.query])

  const onClickHeartTutor = useOnclickHeartTutor({
    isList: true,
    tutorItems: tutors,
    setTutorItems: setTutors,
  })

  const onClickHeartTutorTab = async (e: React.MouseEvent, tutor, type, showToast = true) => {
    onClickHeartTutor(e, tutor, type, showToast)
  }

  useEffect(() => {
    setIsFiltered(false)
    fetchTutorList(1)
    setActiveIndex(0)
  }, [type])

  const handleFilter = () => {
    setShowFilter(true)
  }

  const handleApply = async (e, payload) => {
    setIsFiltered(true)
    const tempPayload = {
      search: keyword,
      gender: payload.gender,
      major: payload.major,
      accent: payload.accent,
      type: getTutorType(type),
      page: 1,
      size: 12,
    }
    const url = apis.tutor.search()
    const response = await axiosPost(url, tempPayload)
    if (response?.result) {
      setTutors(response.result)
      setCount(response.count)
    }
  }

  const postsPerPage = 12
  const totalPosts = count
  const [activeIndex, setActiveIndex] = useState(0)

  const fetchTutorList = async (pageParam, size = 12) => {
    const tempPayload = {
      search: keyword,
      gender: null,
      major: null,
      accent: null,
      type: getTutorType(type),
      page: pageParam,
      size: size,
    }
    const url = apis.tutor.search()
    const response = await axiosPost(url, tempPayload)
    if (response?.result) {
      setTutors(response.result)
      setCount(response.count)
    }
  }
  const handlePagination = (number) => {
    setActiveIndex(number - 1)
    fetchTutorList(number, 12)
  }
  const startIndex = activeIndex * postsPerPage
  const endIndex = startIndex + postsPerPage

  const handleResetFilter = () => {
    setIsFiltered(false)
    fetchTutorList(1)
  }

  return result ? (
    <div className="w-full px-[16px] lg:mt-[20px] lg:max-w-[688px] lg:px-[32px] mx-auto">
      <CompletedLesson result={result} />
    </div>
  ) : (
    <>
      <section className="mt-[20px] max-w-[688px] px-[16px] lg:mt-[40px] lg:px-[0px]">
        <div className="flex items-center justify-between">
          <div className="Hero4-32Bd hidden lg:block">{t(n_tutor_lists.search_title)}</div>
        </div>
        <div>
          <div className="flex h-[48px] items-center rounded-[6px] border-[1px] border-gray-200 bg-gray-50 p-[12px] lg:mt-[32px]">
            <QIcon_search_default width={24} height={24} color={COLORS.GRAY500} className="mr-[8px]" />
            <input
              className={classNames(
                'Body2-14Md w-full flex-grow cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none',
                !isMobile && 'Body1-16Md',
                isMobile && 'Body2-14Md'
              )}
              ref={focusRef}
              value={keyword}
              onChange={onChange}
              placeholder={t(n_tutor_lists.search_placehoder)}
              draggable={false}
              onKeyDown={handleKeyDown}
            />
            {keyword && (
              <div className="cursor-pointer self-center rounded-full bg-gray-200 p-[2px]" onClick={handleClose}>
                <QIcon_close_default color="#FFFFFF" width={16} height={16} />
              </div>
            )}
          </div>
        </div>
        {router?.query?.keyword && (
          <div className="Body1-16Md lg:Headline2-24Md mt-[40px]">
            {t(n_tutor_lists.search_keyword_title(`'${router?.query?.keyword}'`))}
          </div>
        )}
        <div className="mt-[16px] px-[16px] lg:mt-[32px] lg:px-[0px]">
          <div className="mt-[16px] flex items-center justify-between">
            <div className="lg:Title1-20Bd Body2-14Bd">{t(n_book_lessons.total_tutor_number(count))}</div>
            <div className="w-full max-w-[88px]">
              {isFiltered ? (
                <DSButton
                  customBorderColor={COLORS.PURPLE600}
                  customBgColor={COLORS.PURPLE50}
                  isIconMode={true}
                  size={isMobile ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
                  type={BUTTON_TYPE.OUTLINE}
                  onClick={handleFilter}
                >
                  <DSButton.Icon alt={'filter icon'}>
                    <QIcon_filter color={COLORS.PURPLE600} width={16} height={16} />
                  </DSButton.Icon>
                  <DSButton.Label customTxtColor={COLORS.PURPLE600} className="ml-[4px]">
                    {t(n_book_lessons.filter.title)}
                  </DSButton.Label>
                </DSButton>
              ) : (
                <DSButton
                  isIconMode={true}
                  size={isMobile ? BUTTON_SIZE.SM : BUTTON_SIZE.MD}
                  type={BUTTON_TYPE.OUTLINE}
                  onClick={handleFilter}
                >
                  <DSButton.Icon alt={'filter icon'}>
                    <QIcon_filter width={16} height={16} />
                  </DSButton.Icon>
                  <DSButton.Label className="ml-[4px]">{t(n_book_lessons.filter.title)}</DSButton.Label>
                </DSButton>
              )}
            </div>
          </div>
          {loading ? (
            <div className="mt-[16px]">
              <LoadingSpinner show lg primary />
            </div>
          ) : (
            <div className="mt-[16px]">
              <>
                {tutors.length == 0 && isFiltered && (
                  <div className="mt-[16px] flex w-full flex-col items-center justify-center">
                    <div className="relative">
                      <div className="absolute left-[-2px] top-[-2px]">
                        <QIcon_question_filled color={COLORS.GRAY400} width={20} height={20} />
                      </div>
                      <QIcon_person width={48} height={48} color={COLORS.GRAY300} />
                    </div>
                    <div className="Title2-18Md mt-[20px] text-center text-gray-900">
                      {t(n_book_lessons.filter.empty_desc_title)}
                    </div>
                    <div className="Body2-14Md text-gray-500">{t(n_book_lessons.filter.empty_desc_subitle)}</div>
                    <div className="mx-auto mt-[40px] max-w-[136px]">
                      <DSButton
                        className="whitespace-nowrap"
                        size={BUTTON_SIZE.MD}
                        type={BUTTON_TYPE.OUTLINE}
                        onClick={handleResetFilter}
                        isIconMode={true}
                      >
                        <DSButton.Icon alt="refresh">
                          <QIcon_refresh width={16} height={16} />
                        </DSButton.Icon>
                        <DSButton.Label customTxtColor={COLORS.GRAY600}>필터 초기화</DSButton.Label>
                      </DSButton>
                    </div>
                  </div>
                )}
                {tutors.length == 0 && !isFiltered && (
                  <div className="mt-[24px] flex w-full max-w-[688px] flex-col items-center justify-center py-[40px]">
                    <div className="relative">
                      <div className="absolute left-[-2px] top-[-2px]">
                        <QIcon_question_filled color={COLORS.GRAY400} width={20} height={20} />
                      </div>
                      <QIcon_person width={48} height={48} color={COLORS.GRAY300} />
                    </div>
                    <div className="Title2-18Md mt-[20px] text-center text-gray-900">
                      {t(n_tutor_lists.search_keyword_empty_desc(router?.query?.keyword))}
                    </div>
                    <div className="mx-auto mt-[40px] max-w-[184px]">
                      <DSButton
                        className="whitespace-nowrap"
                        size={BUTTON_SIZE.MD}
                        type={BUTTON_TYPE.OUTLINE}
                        customBgColor={COLORS.GRAY100}
                        customBorderColor={COLORS.GRAY100}
                        onClick={() => router.back()}
                      >
                        <DSButton.Label customTxtColor={COLORS.GRAY600}>
                          {t(n_tutor_lists.search_keyword_empty_btn)}
                        </DSButton.Label>
                      </DSButton>
                    </div>
                  </div>
                )}
                <div className="mb-[40px] grid grid-cols-1 gap-[16px]">
                  {tutors.map((tutor: ITutor) => (
                    <TutorCard
                      isRecommend={type == TUTOR_TYPE.RECOMMEND_TUTOR}
                      key={tutor.id}
                      currentUser={currentUser}
                      selected={selectedTutor == tutor.id}
                      handleAvatarClick={(e, tutor) => handleSchedule(e, tutor, TUTOR_PROFILE_TAB.SUMMARY)}
                      handleAvailableButtonClick={(e, tutor) => handleSchedule(e, tutor, TUTOR_PROFILE_TAB.SCHEDULE)}
                      onClickHeart={(e) => onClickHeartTutorTab(e, tutor, type)}
                      tutor={tutor}
                    />
                  ))}
                </div>
              </>

              <div className="flex flex-row items-center justify-center gap-[8px] px-[20px] pb-[20px]">
                <Pagination
                  postsPerPage={postsPerPage}
                  totalPosts={totalPosts}
                  activeIndex={activeIndex}
                  paginate={handlePagination}
                  setActiveIndex={setActiveIndex}
                />
              </div>
            </div>
          )}
        </div>
        {isMobile && <FilterBottomSheet setIsOpen={setShowFilter} isOpen={showFilter} handleApply={handleApply} />}
        {!isMobile && <FilterPopup setIsOpen={setShowFilter} isOpen={showFilter} handleApply={handleApply} />}
      </section>
    </>
  )
}

export default TutorSearchListBody
